import React, { useState, useEffect, useRef } from 'react';
import { apiClient, getErrorMessage } from '@/App';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Euro, FileDown, CheckSquare, Trash2, Check, Clock, AlertCircle, Send, PiggyBank, Calculator } from 'lucide-react';
import html2pdf from 'html2pdf.js';

// ========== COMPOSANT VUE COPROPRIÉTAIRE ==========
const MesAppelsFonds = ({ user }) => {
  const [mesAppels, setMesAppels] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMesAppels();
  }, []);

  const fetchMesAppels = async () => {
    try {
      const response = await apiClient.get('/appels-fonds/mes-appels');
      setMesAppels(response.data || []);
    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setLoading(false);
    }
  };

  const declarerPaiement = async (afId) => {
    try {
      await apiClient.post(`/appels-fonds/${afId}/declarer-paiement`);
      toast.success('Paiement déclaré ! En attente de validation par le trésorier.');
      fetchMesAppels();
    } catch (error) {
      const message = getErrorMessage(error, 'Erreur lors de la déclaration');
      if (message) toast.error(message);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  const getStatutInfo = (statut) => {
    switch (statut) {
      case 'paye':
        return { label: 'Payé', color: 'bg-emerald-100 text-emerald-700', icon: Check };
      case 'a_valider':
        return { label: 'En attente de validation', color: 'bg-amber-100 text-amber-700', icon: Clock };
      default:
        return { label: 'À payer', color: 'bg-red-100 text-red-700', icon: AlertCircle };
    }
  };

  return (
    <div className="fade-in" data-testid="mes-appels-fonds-page">
      <div className="mb-6 md:mb-8">
        <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-2">Mes appels de fonds</h1>
        <p className="text-slate-500 text-base md:text-lg">Consultez vos appels de fonds et déclarez vos paiements</p>
      </div>

      {mesAppels.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-12 text-center">
          <Euro className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500">Aucun appel de fonds vous concernant</p>
        </div>
      ) : (
        <div className="space-y-4">
          {mesAppels.map((appel) => {
            const paiement = appel.mon_paiement;
            const statutInfo = getStatutInfo(paiement?.statut);
            const StatutIcon = statutInfo.icon;

            return (
              <div
                key={appel.id}
                className="bg-white border border-slate-200 rounded-xl shadow-sm p-5"
              >
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div className="flex-1">
                    <h3 className="font-semibold text-slate-900 text-lg mb-2">{appel.description}</h3>
                    <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-slate-600">
                      <p>
                        <span className="text-slate-400">Montant :</span>{' '}
                        <span className="font-semibold text-slate-900">{paiement?.montant_du?.toFixed(2) || '0.00'} €</span>
                      </p>
                      {appel.date_echeance && (
                        <p>
                          <span className="text-slate-400">Échéance :</span>{' '}
                          <span className="font-medium">{format(new Date(appel.date_echeance), 'dd MMMM yyyy', { locale: fr })}</span>
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    {/* Badge de statut */}
                    <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${statutInfo.color}`}>
                      <StatutIcon className="w-4 h-4" />
                      <span className="text-sm font-medium">{statutInfo.label}</span>
                    </div>

                    {/* Bouton déclarer paiement */}
                    {paiement?.statut === 'en_attente' && (
                      <Button
                        onClick={() => declarerPaiement(appel.id)}
                        className="bg-amber-500 hover:bg-amber-600 text-white"
                        data-testid={`declarer-paiement-${appel.id}`}
                      >
                        <Send className="w-4 h-4 mr-2" />
                        Paiement effectué
                      </Button>
                    )}
                  </div>
                </div>

                {paiement?.statut === 'a_valider' && (
                  <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-sm text-amber-700">
                      <Clock className="w-4 h-4 inline mr-1" />
                      Vous avez déclaré ce paiement le {format(new Date(paiement.date_declaration || new Date()), 'dd/MM/yyyy')}. 
                      Le trésorier doit valider la réception sur le compte bancaire de la copropriété.
                    </p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Note informative */}
      <div className="mt-6 p-4 bg-slate-50 border border-slate-200 rounded-lg">
        <h4 className="font-medium text-slate-900 mb-2">Comment ça marche ?</h4>
        <ol className="text-sm text-slate-600 space-y-1 list-decimal list-inside">
          <li>Effectuez votre virement bancaire vers le compte de la copropriété</li>
          <li>Cliquez sur <span className="font-medium">"Paiement effectué"</span> pour déclarer votre paiement</li>
          <li>Le trésorier vérifiera la réception et validera votre paiement</li>
          <li>Votre statut passera alors à <span className="font-medium text-emerald-600">"Payé"</span></li>
        </ol>
      </div>
    </div>
  );
};

// ========== COMPOSANT DOCUMENT APPEL DE FONDS ==========
const AFDocument = ({ selectedAFs, copropriete, copro, postesDepenses, isEditable = false, onMontantChange }) => {
  const [editableMontants, setEditableMontants] = useState({});
  
  useEffect(() => {
    // Initialiser les montants éditables
    if (selectedAFs && copro) {
      const initial = {};
      selectedAFs.forEach(af => {
        const paiement = af.paiements?.[copro.id];
        if (paiement) {
          initial[af.id] = {
            montant: paiement.montant_du || 0,
            details: paiement.details || []
          };
        }
      });
      setEditableMontants(initial);
    }
  }, [selectedAFs, copro]);
  
  if (!copro || selectedAFs.length === 0) return null;

  const handleMontantChange = (afId, newMontant) => {
    const updated = {
      ...editableMontants,
      [afId]: { ...editableMontants[afId], montant: parseFloat(newMontant) || 0 }
    };
    setEditableMontants(updated);
    if (onMontantChange) onMontantChange(updated);
  };

  const totalGeneral = Object.values(editableMontants).reduce((sum, item) => sum + (item?.montant || 0), 0);

  return (
    <div className="bg-white p-8 text-sm" style={{ fontFamily: 'Arial, sans-serif' }}>
      {/* En-tête */}
      <div className="mb-8 border-b-2 border-slate-800 pb-4">
        <h1 className="text-xl font-bold text-slate-900">{copropriete?.nom}</h1>
        <p className="text-slate-600">{copropriete?.adresse}</p>
      </div>

      {/* Destinataire */}
      <div className="text-right mb-8">
        <p className="font-semibold">{copro.prenom} {copro.nom}</p>
        <p className="text-slate-600">Lot n° {copro.lot}</p>
        <p className="text-slate-600">Tantièmes généraux: {copro.tantiemes}</p>
      </div>

      {/* Date */}
      <p className="mb-6">Le {format(new Date(), 'dd MMMM yyyy', { locale: fr })}</p>

      {/* Titre */}
      <h2 className="text-lg font-bold mb-6 text-center">APPEL DE FONDS</h2>

      {/* Pour chaque appel de fonds */}
      {selectedAFs.map(af => {
        const paiement = af.paiements?.[copro.id];
        const details = paiement?.details || [];
        const currentMontant = editableMontants[af.id]?.montant ?? paiement?.montant_du ?? 0;

        return (
          <div key={af.id} className="mb-6 border rounded-lg overflow-hidden">
            {/* En-tête de l'appel */}
            <div className="bg-slate-100 p-3 border-b">
              <p className="font-semibold">{af.description}</p>
              <p className="text-xs text-slate-500">Échéance: {af.date_echeance ? format(new Date(af.date_echeance), 'dd/MM/yyyy') : 'N/A'}</p>
            </div>
            
            {/* Tableau détaillé par poste */}
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-slate-50 text-xs">
                  <th className="border-b p-2 text-left">Poste de dépense</th>
                  <th className="border-b p-2 text-center">Vos tantièmes</th>
                  <th className="border-b p-2 text-center">Total tantièmes</th>
                  <th className="border-b p-2 text-right">Quote-part</th>
                </tr>
              </thead>
              <tbody>
                {details.length > 0 ? (
                  details.map((detail, idx) => (
                    <tr key={idx} className="text-sm">
                      <td className="border-b p-2">{detail.poste_nom}</td>
                      <td className="border-b p-2 text-center">{detail.tantiemes}</td>
                      <td className="border-b p-2 text-center">{detail.total_tantiemes || 1000}</td>
                      <td className="border-b p-2 text-right">{detail.montant?.toFixed(2)} €</td>
                    </tr>
                  ))
                ) : (
                  <tr className="text-sm">
                    <td className="border-b p-2" colSpan="3">Charges générales</td>
                    <td className="border-b p-2 text-right">{currentMontant.toFixed(2)} €</td>
                  </tr>
                )}
              </tbody>
              <tfoot>
                <tr className="bg-emerald-50 font-semibold">
                  <td colSpan="3" className="p-2 text-right">Sous-total</td>
                  <td className="p-2 text-right">
                    {isEditable ? (
                      <input
                        type="number"
                        step="0.01"
                        value={currentMontant}
                        onChange={(e) => handleMontantChange(af.id, e.target.value)}
                        className="w-24 text-right border rounded px-2 py-1"
                      />
                    ) : (
                      <span>{currentMontant.toFixed(2)} €</span>
                    )}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        );
      })}

      {/* Total général */}
      <div className="border-t-2 border-slate-800 pt-4 mt-4">
        <div className="flex justify-between items-center text-lg font-bold">
          <span>TOTAL À PAYER</span>
          <span className="text-emerald-700">{totalGeneral.toFixed(2)} €</span>
        </div>
      </div>

      {/* Instructions */}
      <div className="text-sm text-slate-600 mt-8 pt-4 border-t">
        <p className="font-semibold mb-2">Modalités de paiement :</p>
        <p>Merci de procéder au règlement avant la date d'échéance indiquée.</p>
        <p className="mt-2">Pour toute question, contactez le syndic de copropriété.</p>
      </div>
    </div>
  );
};

// ========== COMPOSANT VUE ADMIN (Président/Trésorier) ==========
const AppelsFondsAdmin = ({ user }) => {
  const [appelsFonds, setAppelsFonds] = useState([]);
  const [copropriete, setCopropriete] = useState(null);
  const [coproprietaires, setCoproprietaires] = useState([]);
  const [postesDepenses, setPostesDepenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createModal, setCreateModal] = useState(false);
  const [generateModal, setGenerateModal] = useState(false);
  const [detailModal, setDetailModal] = useState(false);
  const [selectedAF, setSelectedAF] = useState(null);
  const [selectedAFs, setSelectedAFs] = useState([]);
  const [selectedCopro, setSelectedCopro] = useState('');
  const documentRef = useRef(null);
  
  // Budget prévisionnel
  const [budgetValide, setBudgetValide] = useState(false);
  const [showBudgetSection, setShowBudgetSection] = useState(false);
  const [budgetTrimestre, setBudgetTrimestre] = useState(1);
  const [budgetYear, setBudgetYear] = useState(new Date().getFullYear());
  const [budgetPreview, setBudgetPreview] = useState(null);
  const [creatingBudgetADF, setCreatingBudgetADF] = useState(false);

  const [newAF, setNewAF] = useState({
    poste_depense_id: '',
    montant_total: '',
    date_debut: '',
    date_fin: '',
    description: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [afResponse, coproResponse, copropsResponse] = await Promise.all([
        apiClient.get('/appels-fonds'),
        apiClient.get(`/coproprietes/${user.copropriete_active_id}`),
        apiClient.get('/coproprietaires')
      ]);
      setAppelsFonds(afResponse.data);
      setCopropriete(coproResponse.data);
      setPostesDepenses(coproResponse.data.postes_depenses || []);
      setCoproprietaires(copropsResponse.data);
      
      // Vérifier si un budget est validé (pour l'année en cours ou l'année précédente)
      try {
        const currentYear = new Date().getFullYear();
        let budgetRes = await apiClient.get(`/coproprietes/${user.copropriete_active_id}/budget?annee=${currentYear}`);
        if (!budgetRes.data?.valide) {
          // Si pas de budget validé pour cette année, vérifier l'année précédente
          budgetRes = await apiClient.get(`/coproprietes/${user.copropriete_active_id}/budget?annee=${currentYear - 1}`);
          if (budgetRes.data?.valide) {
            setBudgetYear(currentYear - 1);
          }
        }
        setBudgetValide(budgetRes.data?.valide || false);
      } catch (e) {
        setBudgetValide(false);
      }
    } catch (error) {
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchBudgetPreview = async () => {
    try {
      const response = await apiClient.get(
        `/coproprietes/${user.copropriete_active_id}/budget/adf-previsionnel?annee=${budgetYear}&trimestre=${budgetTrimestre}`
      );
      setBudgetPreview(response.data);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erreur lors du chargement'));
      setBudgetPreview(null);
    }
  };
  
  const createBudgetADF = async () => {
    setCreatingBudgetADF(true);
    try {
      await apiClient.post(
        `/coproprietes/${user.copropriete_active_id}/budget/creer-adf?annee=${budgetYear}&trimestre=${budgetTrimestre}`
      );
      toast.success(`Appel de fonds T${budgetTrimestre} ${budgetYear} créé avec succès !`);
      setShowBudgetSection(false);
      setBudgetPreview(null);
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erreur lors de la création'));
    } finally {
      setCreatingBudgetADF(false);
    }
  };

  const handleCreateAF = async () => {
    if (!newAF.poste_depense_id || !newAF.montant_total || !newAF.description || !newAF.date_fin) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    try {
      await apiClient.post('/appels-fonds', {
        postes_depenses_ids: [newAF.poste_depense_id],
        montant_total: parseFloat(newAF.montant_total),
        date_echeance: new Date(newAF.date_fin).toISOString(),
        description: newAF.description,
      });
      toast.success('Appel de fonds créé');
      setCreateModal(false);
      setNewAF({ poste_depense_id: '', montant_total: '', date_debut: '', date_fin: '', description: '' });
      fetchData();
    } catch (error) {
      const message = getErrorMessage(error, "Erreur lors de la création");
      if (message) toast.error(message);
    }
  };

  const toggleSelection = (afId) => {
    setSelectedAFs(prev => 
      prev.includes(afId) ? prev.filter(id => id !== afId) : [...prev, afId]
    );
  };

  const selectAll = () => setSelectedAFs(appelsFonds.map(af => af.id));
  const deselectAll = () => setSelectedAFs([]);

  const generatePDF = async () => {
    if (!selectedCopro || !documentRef.current) return;

    const element = documentRef.current;
    const opt = {
      margin: 10,
      filename: `appel_fonds_${selectedCopro}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2 },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
    };

    try {
      await html2pdf().set(opt).from(element).save();
      toast.success('PDF généré avec succès');
    } catch (error) {
      toast.error('Erreur lors de la génération du PDF');
    }
  };

  const handleDelete = async (afId, description) => {
    if (!window.confirm(`Supprimer "${description}" ?`)) return;

    try {
      await apiClient.delete(`/appels-fonds/${afId}`);
      toast.success('Appel de fonds supprimé');
      setSelectedAFs(prev => prev.filter(id => id !== afId));
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const openDetailModal = (af) => {
    setSelectedAF(af);
    setDetailModal(true);
  };

  const togglePaiement = async (afId, userId, currentStatus) => {
    // Si le statut actuel est "a_valider", on valide en "paye"
    // Sinon on toggle entre "paye" et "en_attente"
    let newStatus;
    if (currentStatus === 'a_valider') {
      newStatus = 'paye';
    } else if (currentStatus === 'paye') {
      newStatus = 'en_attente';
    } else {
      newStatus = 'paye';
    }

    try {
      await apiClient.put(`/appels-fonds/${afId}/paiement/${userId}`, {
        statut: newStatus,
        date_paiement: newStatus === 'paye' ? new Date().toISOString() : null
      });
      toast.success(newStatus === 'paye' ? 'Paiement validé' : 'Paiement annulé');
      fetchData();
      const res = await apiClient.get('/appels-fonds');
      const updated = res.data.find(a => a.id === afId);
      setSelectedAF(updated);
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const getUserName = (userId) => {
    const u = coproprietaires.find(c => c.id === userId);
    return u ? `${u.prenom} ${u.nom}` : 'Inconnu';
  };

  const getUserLot = (userId) => {
    const u = coproprietaires.find(c => c.id === userId);
    return u?.lot || 'N/A';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <>
      <div className="fade-in" data-testid="appels-fonds-page">
        <div className="mb-6 md:mb-8 flex flex-col gap-4">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-2">Appels de fonds</h1>
            <p className="text-slate-500 text-base md:text-lg">Génération par copropriétaire</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Button
              onClick={() => setCreateModal(true)}
              className="bg-slate-900 text-white hover:bg-slate-800 btn-hover"
            >
              <Plus className="w-4 h-4 mr-2" />
              Créer un appel
            </Button>
            {budgetValide && (
              <Button
                onClick={() => { setShowBudgetSection(true); fetchBudgetPreview(); }}
                variant="outline"
                className="border-emerald-500 text-emerald-700 hover:bg-emerald-50"
              >
                <PiggyBank className="w-4 h-4 mr-2" />
                Créer depuis le budget prévisionnel
              </Button>
            )}
          </div>
        </div>

        {/* Section Budget Prévisionnel */}
        {showBudgetSection && budgetValide && (
          <div className="mb-6 bg-emerald-50 border-2 border-emerald-200 rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                  <Calculator className="w-6 h-6 text-emerald-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-emerald-900">Appel de fonds prévisionnel</h3>
                  <p className="text-sm text-emerald-700">Basé sur votre budget validé</p>
                </div>
              </div>
              <button onClick={() => { setShowBudgetSection(false); setBudgetPreview(null); }} className="text-slate-500 hover:text-slate-700">✕</button>
            </div>
            
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-emerald-800 mb-1">Année</label>
                <select 
                  value={budgetYear} 
                  onChange={(e) => { setBudgetYear(parseInt(e.target.value)); setBudgetPreview(null); }}
                  className="w-full px-3 py-2 border border-emerald-300 rounded-lg bg-white"
                >
                  {[2024, 2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-emerald-800 mb-1">Trimestre</label>
                <select 
                  value={budgetTrimestre} 
                  onChange={(e) => { setBudgetTrimestre(parseInt(e.target.value)); setBudgetPreview(null); }}
                  className="w-full px-3 py-2 border border-emerald-300 rounded-lg bg-white"
                >
                  <option value={1}>T1 (Jan-Mar)</option>
                  <option value={2}>T2 (Avr-Juin)</option>
                  <option value={3}>T3 (Juil-Sept)</option>
                  <option value={4}>T4 (Oct-Déc)</option>
                </select>
              </div>
            </div>
            
            <Button onClick={fetchBudgetPreview} variant="outline" className="mb-4 w-full border-emerald-400 text-emerald-700">
              Calculer la répartition
            </Button>
            
            {budgetPreview && (
              <div className="bg-white rounded-lg border border-emerald-200 overflow-hidden">
                <div className="p-4 bg-emerald-100 border-b border-emerald-200">
                  <p className="font-semibold text-emerald-900">
                    Appel T{budgetPreview.trimestre} {budgetPreview.annee} - Total : {budgetPreview.total_adf?.toFixed(2)} €
                  </p>
                </div>
                <div className="max-h-96 overflow-y-auto divide-y">
                  {(budgetPreview.repartition || []).map((r, idx) => {
                    const details = r.details || [];
                    return (
                      <div key={idx}>
                        <div className="flex justify-between items-center px-4 py-3 bg-slate-50">
                          <span className="font-medium text-slate-900">{r.user_name}</span>
                          <span className="font-bold text-emerald-700">{r.montant_du?.toFixed(2)} €</span>
                        </div>
                        {details.length > 0 && (
                          <div className="px-4 py-2 bg-white text-sm">
                            <div className="grid grid-cols-3 text-slate-500 text-xs font-medium mb-1">
                              <span>Poste</span>
                              <span className="text-center">Tantièmes</span>
                              <span className="text-right">Montant</span>
                            </div>
                            {details.map((d, i) => (
                              <div key={i} className="grid grid-cols-3 py-1 text-slate-700">
                                <span>{d.poste_nom}</span>
                                <span className="text-center">
                                  <span className="bg-slate-100 px-2 py-0.5 rounded text-xs">{d.tantiemes}/1000</span>
                                </span>
                                <span className="text-right font-medium">{d.montant?.toFixed(2)} €</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                <div className="p-4 border-t border-emerald-200">
                  <Button 
                    onClick={createBudgetADF} 
                    disabled={creatingBudgetADF}
                    className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                  >
                    {creatingBudgetADF ? 'Création...' : `Créer l'appel de fonds T${budgetTrimestre}`}
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Actions de sélection */}
        {appelsFonds.length > 0 && (
          <div className="mb-4 flex flex-wrap items-center gap-3 p-4 bg-slate-50 rounded-lg border border-slate-200">
            <span className="text-sm font-medium text-slate-700">
              {selectedAFs.length} sélectionné(s)
            </span>
            <Button onClick={selectAll} size="sm" variant="outline">
              Tout sélectionner
            </Button>
            <Button onClick={deselectAll} size="sm" variant="outline">
              Tout désélectionner
            </Button>
            {selectedAFs.length > 0 && (
              <Button onClick={() => setGenerateModal(true)} size="sm" className="bg-indigo-600 text-white hover:bg-indigo-700">
                <CheckSquare className="w-4 h-4 mr-2" />
                Générer pour un copropriétaire
              </Button>
            )}
          </div>
        )}

        {/* Liste des appels de fonds */}
        <div className="space-y-3">
          {appelsFonds.map((af) => {
            const posteIds = af.postes_depenses_ids || [];
            const postes = postesDepenses.filter(p => posteIds.includes(p.id));
            const posteNoms = postes.map(p => p.nom).join(', ') || 'N/A';
            const isSelected = selectedAFs.includes(af.id);
            
            // Calculer stats paiements
            const paiements = Object.values(af.paiements || {});
            const nbPaye = paiements.filter(p => p.statut === 'paye').length;
            const nbAValider = paiements.filter(p => p.statut === 'a_valider').length;
            const nbTotal = paiements.length;

            return (
              <div
                key={af.id}
                className={`bg-white border rounded-lg shadow-sm p-4 transition-all ${
                  isSelected ? 'border-indigo-500 bg-indigo-50' : 'border-slate-200'
                }`}
              >
                <div className="flex items-start gap-4">
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => toggleSelection(af.id)}
                    className="mt-1 w-5 h-5 rounded border-slate-300"
                  />
                  <div className="flex-1 cursor-pointer" onClick={() => openDetailModal(af)}>
                    <h3 className="font-semibold text-slate-900 mb-1">{af.description}</h3>
                    <div className="text-sm text-slate-600 space-y-1">
                      <p><strong>Poste:</strong> {posteNoms}</p>
                      <p><strong>Montant:</strong> {af.montant_total?.toFixed(2)} €</p>
                      <p><strong>Échéance:</strong> {af.date_echeance ? format(new Date(af.date_echeance), 'dd/MM/yyyy') : 'N/A'}</p>
                      {nbTotal > 0 && (
                        <div className="flex gap-2 items-center flex-wrap">
                          <span className={nbPaye === nbTotal ? 'text-green-600' : 'text-slate-600'}>
                            <strong>Payés:</strong> {nbPaye}/{nbTotal}
                          </span>
                          {nbAValider > 0 && (
                            <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-xs font-medium">
                              {nbAValider} à valider
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => handleDelete(af.id, af.description)}
                    className="text-red-600 hover:text-red-700 transition-colors p-2"
                    title="Supprimer"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              </div>
            );
          })}

          {appelsFonds.length === 0 && (
            <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-12 text-center">
              <Euro className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">Aucun appel de fonds créé</p>
            </div>
          )}
        </div>

        {/* Create Modal */}
        <Dialog open={createModal} onOpenChange={setCreateModal}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Créer un appel de fonds</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <Label>Poste de dépense *</Label>
                <Select
                  value={newAF.poste_depense_id}
                  onValueChange={(value) => setNewAF({ ...newAF, poste_depense_id: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner" />
                  </SelectTrigger>
                  <SelectContent>
                    {postesDepenses.map(p => (
                      <SelectItem key={p.id} value={p.id}>{p.nom}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Description *</Label>
                <Input
                  value={newAF.description}
                  onChange={(e) => setNewAF({ ...newAF, description: e.target.value })}
                  placeholder="Ex: Travaux toiture"
                />
              </div>
              <div>
                <Label>Montant total (€) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={newAF.montant_total}
                  onChange={(e) => setNewAF({ ...newAF, montant_total: e.target.value })}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Date début période *</Label>
                  <Input
                    type="date"
                    value={newAF.date_debut}
                    onChange={(e) => setNewAF({ ...newAF, date_debut: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Date fin période *</Label>
                  <Input
                    type="date"
                    value={newAF.date_fin}
                    onChange={(e) => setNewAF({ ...newAF, date_fin: e.target.value })}
                  />
                </div>
              </div>
              <div className="flex gap-3 mt-6">
                <Button onClick={handleCreateAF} className="flex-1 bg-slate-900 text-white">
                  Créer
                </Button>
                <Button onClick={() => setCreateModal(false)} variant="outline" className="flex-1">
                  Annuler
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Detail Modal - Suivi des paiements */}
        <Dialog open={detailModal} onOpenChange={setDetailModal}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Suivi des paiements</DialogTitle>
            </DialogHeader>
            {selectedAF && (
              <div className="mt-4">
                <div className="bg-slate-50 p-4 rounded-lg mb-4">
                  <h4 className="font-semibold text-slate-900">{selectedAF.description}</h4>
                  <p className="text-sm text-slate-600">Montant total: {selectedAF.montant_total?.toFixed(2)} €</p>
                </div>
                
                <div className="space-y-3 max-h-80 overflow-y-auto">
                  {Object.entries(selectedAF.paiements || {}).map(([userId, paiement]) => {
                    const isAValider = paiement.statut === 'a_valider';
                    const isPaye = paiement.statut === 'paye';
                    
                    return (
                      <div 
                        key={userId} 
                        className={`flex items-center justify-between p-3 rounded-lg border ${
                          isPaye ? 'bg-green-50 border-green-200' : 
                          isAValider ? 'bg-amber-50 border-amber-200' : 
                          'bg-white border-slate-200'
                        }`}
                      >
                        <div>
                          <p className="font-medium text-slate-900">{getUserName(userId)}</p>
                          <p className="text-sm text-slate-500">Lot: {getUserLot(userId)}</p>
                          <p className="text-sm font-semibold">{paiement.montant_du?.toFixed(2)} €</p>
                        </div>
                        <div className="flex flex-col items-end gap-2">
                          {isAValider && (
                            <span className="text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded">
                              Déclaré par le copropriétaire
                            </span>
                          )}
                          <button
                            onClick={() => togglePaiement(selectedAF.id, userId, paiement.statut)}
                            className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-all ${
                              isPaye 
                                ? 'bg-green-600 text-white' 
                                : isAValider
                                  ? 'bg-amber-500 text-white hover:bg-amber-600'
                                  : 'bg-slate-200 text-slate-700 hover:bg-slate-300'
                            }`}
                          >
                            <Check className="w-4 h-4" />
                            {isPaye ? 'Payé' : isAValider ? 'Valider' : 'Non payé'}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Generate Modal */}
        <Dialog open={generateModal} onOpenChange={setGenerateModal}>
          <DialogContent className="max-w-5xl max-h-[95vh] overflow-hidden flex flex-col">
            <DialogHeader>
              <DialogTitle>Générer appel de fonds</DialogTitle>
            </DialogHeader>
            <div className="mb-4">
              <Label>Sélectionner le copropriétaire</Label>
              <Select value={selectedCopro} onValueChange={setSelectedCopro}>
                <SelectTrigger>
                  <SelectValue placeholder="Choisir un copropriétaire" />
                </SelectTrigger>
                <SelectContent>
                  {coproprietaires.map(copro => (
                    <SelectItem key={copro.id} value={copro.id}>
                      {copro.prenom} {copro.nom} - Lot {copro.lot}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {selectedCopro && (
              <>
                <div className="flex-1 overflow-y-auto p-6">
                  <div ref={documentRef}>
                    <AFDocument
                      selectedAFs={selectedAFs.map(id => appelsFonds.find(af => af.id === id)).filter(Boolean)}
                      copropriete={copropriete}
                      copro={coproprietaires.find(c => c.id === selectedCopro)}
                      postesDepenses={postesDepenses}
                      isEditable={true}
                    />
                  </div>
                </div>
                <div className="flex justify-end gap-2 pt-4 border-t">
                  <Button onClick={generatePDF} className="bg-slate-900 text-white">
                    <FileDown className="w-4 h-4 mr-2" />
                    Générer PDF
                  </Button>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </>
  );
};

// ========== COMPOSANT PRINCIPAL ==========
const AppelsFondsV3 = ({ user }) => {
  const canManage = user?.role === 'président' || user?.role === 'trésorier';

  // Si l'utilisateur est président ou trésorier, afficher la vue admin
  // Sinon afficher la vue copropriétaire
  if (canManage) {
    return <AppelsFondsAdmin user={user} />;
  }
  
  return <MesAppelsFonds user={user} />;
};

export default AppelsFondsV3;
